import { typographyTokens } from '../../../../theme/typographyTokens';
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SEPInitiatePhaseRow } from '../../../../../shared/types/SEP';

interface SEPSelectionTableProps {
  sepsData: SEPInitiatePhaseRow[] | undefined;
  filteredSEPs: SEPInitiatePhaseRow[];
  selectedSEPs: number[];
  sepSearch: string;
  sepsLoading: boolean;
  onSepSearchChange: (value: string) => void;
  onSEPSelect: (sepId: number) => void;
  onSelectAllSEPs: () => void;
  onRefresh: () => void;
}

const SEPSelectionTable: React.FC<SEPSelectionTableProps> = ({
  sepsData,
  filteredSEPs,
  selectedSEPs,
  sepSearch,
  sepsLoading,
  onSepSearchChange,
  onSEPSelect,
  onSelectAllSEPs,
  onRefresh,
}) => {
  return (
    <Box sx={{ flex: 1, minWidth: 400 }}>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>
            SEPs in Initiate Phase ({sepsData?.length || 0})
          </Typography>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
          >
            Refresh
          </Button>
        </Box>

        <TextField
          size="small"
          placeholder="Search SEPs by ID, name, or creator..."
          fullWidth
          value={sepSearch}
          onChange={(e) => onSepSearchChange(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {selectedSEPs.length > 0 && (
          <Chip
            label={`${selectedSEPs.length} SEP(s) selected`}
            color="primary"
            size="small"
            sx={{ mb: 2 }}
          />
        )}

        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      filteredSEPs.length > 0 &&
                      selectedSEPs.length === filteredSEPs.length
                    }
                    indeterminate={
                      selectedSEPs.length > 0 &&
                      selectedSEPs.length < filteredSEPs.length
                    }
                    onChange={onSelectAllSEPs}
                  />
                </TableCell>
                <TableCell>SEP ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Creator</TableCell>
                <TableCell align="center">Val. Tasks</TableCell>
                <TableCell align="center">Init. Tasks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSEPs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary">
                      {sepsLoading
                        ? 'Loading...'
                        : 'No SEPs found in initiate phase.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSEPs.map((sep) => (
                  <TableRow
                    key={sep.id}
                    hover
                    selected={selectedSEPs.includes(sep.id)}
                    onClick={() => onSEPSelect(sep.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedSEPs.includes(sep.id)} />
                    </TableCell>
                    <TableCell>{sep.id}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={sep.name || ''}
                      >
                        {sep.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 150,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={sep.creatorEmail || ''}
                      >
                        {sep.creatorName || sep.creatorEmail || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {sep.validationTasksCount}
                    </TableCell>
                    <TableCell align="center">
                      {sep.initiateTasksCount}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default SEPSelectionTable;
